/**
 * Maps every mockup `data-icon` name (mockups/assets/icons.js) to a lucide-react component.
 * Aliases (no exact lucide twin): flask -> FlaskConical, building-school -> School.
 */
import {
  Activity,
  Archive,
  AppWindow,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowUpRight,
  Bell,
  BookOpen,
  Bot,
  Brain,
  Building,
  Building2,
  Calendar,
  ChartNoAxesColumnIncreasing,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronsUpDown,
  Circle,
  CircleAlert,
  CircleCheckBig,
  CircleQuestionMark,
  CircleX,
  Clock,
  CirclePause,
  CloudUpload,
  Code,
  Command,
  Copy,
  ChartColumn,
  Contact,
  Cpu,
  Hourglass,
  Loader,
  OctagonAlert,
  ShieldAlert,
  CreditCard,
  Database,
  Download,
  Ellipsis,
  EllipsisVertical,
  ExternalLink,
  Eye,
  EyeOff,
  File,
  FileSpreadsheet,
  FileText,
  FlaskConical,
  Funnel,
  Gauge,
  GitBranch,
  Globe,
  GraduationCap,
  GripVertical,
  Hash,
  Headset,
  House,
  Image,
  Inbox,
  Info,
  Key,
  Languages,
  Layers,
  LayoutDashboard,
  LifeBuoy,
  Link,
  ListChecks,
  HardHat,
  Lock,
  LockOpen,
  LogIn,
  LogOut,
  Mail,
  MapPin,
  Maximize,
  Megaphone,
  Menu,
  MessageCircle,
  MessageSquare,
  MessagesSquare,
  Mic,
  Minimize,
  Minus,
  Monitor,
  Moon,
  Package,
  Palette,
  PanelLeft,
  Paperclip,
  Pause,
  Pencil,
  Phone,
  Play,
  Plug,
  Plus,
  Quote,
  RefreshCw,
  RotateCcw,
  RotateCcwClock,
  Redo2,
  StickyNote,
  Undo2,
  ScanText,
  School,
  ScrollText,
  Search,
  Send,
  Server,
  Settings,
  Settings2,
  ShoppingCart,
  Shield,
  ShieldCheck,
  SlidersHorizontal,
  Smartphone,
  Sparkles,
  Square,
  Star,
  Sun,
  Table,
  Tag,
  Target,
  ThumbsDown,
  ThumbsUp,
  Ticket,
  Trash,
  TrendingDown,
  TrendingUp,
  TriangleAlert,
  Truck,
  Upload,
  User,
  UserCheck,
  UserPlus,
  Users,
  Wallet,
  Webhook,
  WifiOff,
  Workflow,
  X,
  Zap,
  type LucideIcon,
  Bookmark,
  BookmarkPlus,
  CircleDot,
  Columns3,
  FilterX,
  Flag,
  FolderOpen,
  GitMerge,
  MousePointerClick,
  UserX,
  Keyboard,
  AtSign,
  Bold,
  FileImage,
  Italic,
  Link2,
  List,
  Reply,
  Save,
  Siren,
  Split,
  Bug,
  CalendarClock,
  Camera,
  ClipboardList,
  Hammer,
  Heart,
  Printer,
  Receipt,
  Stethoscope,
  Tags,
  TextCursorInput,
  Thermometer,
  Wrench,
  LayoutList,
  Timer,
  CircleHelp,
  QrCode,
  FileSignature,
  BadgeCheck,
  ClipboardCheck,
  CalendarPlus,
  Move,
  Infinity as InfinityIcon,
} from "lucide-react";
import type { IconName } from "@sagot/constants";

export const ICONS = {
  home: House,
  "layout-dashboard": LayoutDashboard,
  "message-square": MessageSquare,
  "messages-square": MessagesSquare,
  "message-circle": MessageCircle,
  inbox: Inbox,
  database: Database,
  "book-open": BookOpen,
  "file-text": FileText,
  file: File,
  "file-spreadsheet": FileSpreadsheet,
  upload: Upload,
  "upload-cloud": CloudUpload,
  download: Download,
  globe: Globe,
  link: Link,
  settings: Settings,
  "sliders-horizontal": SlidersHorizontal,
  users: Users,
  user: User,
  "user-plus": UserPlus,
  "user-check": UserCheck,
  "bar-chart": ChartNoAxesColumnIncreasing,
  "trending-up": TrendingUp,
  "trending-down": TrendingDown,
  activity: Activity,
  "credit-card": CreditCard,
  wallet: Wallet,
  bot: Bot,
  sparkles: Sparkles,
  zap: Zap,
  plug: Plug,
  webhook: Webhook,
  code: Code,
  palette: Palette,
  shield: Shield,
  "shield-check": ShieldCheck,
  lock: Lock,
  unlock: LockOpen,
  key: Key,
  mail: Mail,
  smartphone: Smartphone,
  monitor: Monitor,
  "app-window": AppWindow,
  bell: Bell,
  megaphone: Megaphone,
  search: Search,
  plus: Plus,
  minus: Minus,
  x: X,
  check: Check,
  "check-circle": CircleCheckBig,
  "x-circle": CircleX,
  "alert-triangle": TriangleAlert,
  "alert-circle": CircleAlert,
  info: Info,
  "help-circle": CircleQuestionMark,
  "chevron-down": ChevronDown,
  "chevron-up": ChevronUp,
  "chevron-left": ChevronLeft,
  "chevron-right": ChevronRight,
  "chevrons-up-down": ChevronsUpDown,
  "arrow-right": ArrowRight,
  "arrow-left": ArrowLeft,
  "arrow-up": ArrowUp,
  "arrow-up-right": ArrowUpRight,
  "external-link": ExternalLink,
  "more-horizontal": Ellipsis,
  "more-vertical": EllipsisVertical,
  trash: Trash,
  pencil: Pencil,
  copy: Copy,
  "refresh-cw": RefreshCw,
  "rotate-ccw": RotateCcw,
  "undo-2": Undo2,
  "redo-2": Redo2,
  "sticky-note": StickyNote,
  filter: Funnel,
  calendar: Calendar,
  clock: Clock,
  history: RotateCcwClock,
  eye: Eye,
  "eye-off": EyeOff,
  "log-out": LogOut,
  "log-in": LogIn,
  sun: Sun,
  moon: Moon,
  send: Send,
  paperclip: Paperclip,
  "thumbs-up": ThumbsUp,
  "thumbs-down": ThumbsDown,
  headset: Headset,
  "graduation-cap": GraduationCap,
  building: Building,
  target: Target,
  flask: FlaskConical,
  "list-checks": ListChecks,
  server: Server,
  cpu: Cpu,
  layers: Layers,
  package: Package,
  "scroll-text": ScrollText,
  "life-buoy": LifeBuoy,
  tag: Tag,
  image: Image,
  mic: Mic,
  languages: Languages,
  maximize: Maximize,
  minimize: Minimize,
  menu: Menu,
  "panel-left": PanelLeft,
  star: Star,
  "scan-text": ScanText,
  table: Table,
  gauge: Gauge,
  hash: Hash,
  circle: Circle,
  pause: Pause,
  play: Play,
  square: Square,
  "grip-vertical": GripVertical,
  "map-pin": MapPin,
  phone: Phone,
  "wifi-off": WifiOff,
  quote: Quote,
  "git-branch": GitBranch,
  workflow: Workflow,
  brain: Brain,
  command: Command,
  "building-school": School,
  ticket: Ticket,
  "building-2": Building2,
  contact: Contact,
  "hard-hat": HardHat,
  truck: Truck,
  "shopping-cart": ShoppingCart,
  "chart-column": ChartColumn,
  "settings-2": Settings2,
  loader: Loader,
  hourglass: Hourglass,
  "pause-circle": CirclePause,
  archive: Archive,
  "shield-alert": ShieldAlert,
  "alert-octagon": OctagonAlert,
  "arrow-down": ArrowDown,
  // Tickets list (SD-S1-43): views rail, filters, bulk bar.
  "bookmark": Bookmark,
  "bookmark-plus": BookmarkPlus,
  "circle-dot": CircleDot,
  "columns-3": Columns3,
  "filter-x": FilterX,
  "flag": Flag,
  "folder-open": FolderOpen,
  "git-merge": GitMerge,
  "mouse-pointer-click": MousePointerClick,
  "user-x": UserX,
  keyboard: Keyboard,
  // Ticket workspace (SD-S1-44): composer toolbar, attachments, merge and escalation.
  "at-sign": AtSign,
  bold: Bold,
  "file-image": FileImage,
  italic: Italic,
  "link-2": Link2,
  list: List,
  reply: Reply,
  save: Save,
  siren: Siren,
  split: Split,
  // Service settings (SD-S1-47): ticket type icons the API accepts, field type and tab icons.
  "bug": Bug,
  "calendar-clock": CalendarClock,
  "camera": Camera,
  "clipboard-list": ClipboardList,
  "hammer": Hammer,
  "heart": Heart,
  "printer": Printer,
  "receipt": Receipt,
  "stethoscope": Stethoscope,
  "tags": Tags,
  "text-cursor-input": TextCursorInput,
  "thermometer": Thermometer,
  "triangle-alert": TriangleAlert,
  "wrench": Wrench,
  "layout-list": LayoutList,
  "timer": Timer,
  "circle-help": CircleHelp,
  // After-sales screens (SD-S3-10): assets, contracts, installations.
  "qr-code": QrCode,
  "file-signature": FileSignature,
  "badge-check": BadgeCheck,
  "clipboard-check": ClipboardCheck,
  "calendar-plus": CalendarPlus,
  move: Move,
  infinity: InfinityIcon,
} as const satisfies Record<IconName, LucideIcon>;

export type { IconName, LucideIcon };
